// 홈 첫 화면 '다가오는 일정' 카드 — 등록된 일정을 클릭 없이 바로 보여준다.
//
// 기존에는 헤더의 달력 아이콘을 눌러야 일정을 볼 수 있어 기능이 있는지도 모르는
// 사용자가 많았다. 목록이 비어 있어도 카드를 숨기지 않는다 — 빈 상태가 곧 안내다.
import { cn } from "@/lib/cn";
import type { ScheduleItem } from "@/api/types";
import { fieldBadgeClass, fmtRelativeDay, fmtTime } from "./utils";
import { CalendarDays, ChevronRight, MapPin, Plus, Repeat } from "lucide-react";

/** 카드에 접어 보여줄 최대 건수 — 나머지는 '전체 보기'로. */
const MAX_ROWS = 5;

interface Props {
  items: ScheduleItem[];
  loading: boolean;
  /** 전체 보기 — 일정 패널 열기. item 을 주면 그 일정 상세까지 펼친다. */
  onOpen: (item?: ScheduleItem) => void;
}

export default function UpcomingSchedules({ items, loading, onOpen }: Props) {
  const rows = items.slice(0, MAX_ROWS);
  const more = items.length - rows.length;

  return (
    <section className="rounded-xl border border-border bg-card p-3">
      <div className="mb-2 flex items-center gap-1.5">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <CalendarDays className="h-4 w-4 text-[#0E1E2D]" /> 다가오는 일정
        </h3>
        <div className="flex-1" />
        <button
          onClick={() => onOpen()}
          className="flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-semibold text-[#0E1E2D] hover:bg-muted"
        >
          전체 보기 <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {loading && items.length === 0 ? (
        // 스켈레톤 — 첫 화면에서 카드 높이가 튀지 않게 자리만 잡아둔다.
        <div className="space-y-1.5" aria-hidden>
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-8 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-wrap items-center gap-2 px-1 py-1.5">
          <p className="text-sm text-muted-foreground">
            등록된 일정이 없습니다. 모임·회의 일정을 등록해 함께 보세요.
          </p>
          <button
            onClick={() => onOpen()}
            className="flex items-center gap-1 rounded-lg bg-[#0E1E2D] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#1C3954]"
          >
            <Plus className="h-3.5 w-3.5" /> 일정 등록
          </button>
        </div>
      ) : (
        <>
          <ul className="space-y-0.5">
            {rows.map((s) => {
              const isToday = fmtRelativeDay(s.start_at) === "오늘";
              return (
                <li key={s.id}>
                  <button
                    onClick={() => onOpen(s)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted"
                  >
                    <span
                      className={cn(
                        "w-[52px] shrink-0 text-[12px] font-semibold tabular-nums",
                        isToday ? "text-[#0E1E2D]" : "text-muted-foreground",
                      )}
                    >
                      {fmtRelativeDay(s.start_at)}
                    </span>
                    <span className="w-[38px] shrink-0 text-[12px] tabular-nums text-muted-foreground">
                      {fmtTime(s.start_at)}
                    </span>
                    <span
                      className={cn(
                        "hidden shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold sm:inline",
                        fieldBadgeClass(s.field),
                      )}
                    >
                      {s.field}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm">{s.title}</span>
                    {s.location && (
                      <span className="hidden max-w-[110px] shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground sm:flex">
                        <MapPin className="h-3 w-3 shrink-0" />
                        <span className="truncate">{s.location}</span>
                      </span>
                    )}
                    {s.repeat_group && (
                      <Repeat
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                        aria-label="반복 일정"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          {more > 0 && (
            <button
              onClick={() => onOpen()}
              className="mt-1 w-full rounded-md px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted"
            >
              외 {more}건 더 보기
            </button>
          )}
        </>
      )}
    </section>
  );
}
